import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

import {
  IMAGE_TEMPLATES_INFO,
  VIDEO_TEMPLATES_INFO,
  IMAGE_TEMPLATES,
  OneToneImage,
  VIDEO_TEMPLATES
} from "@talentbait/remotion-templates-talentbait";

import {
  AdAssistantBody,
  AdAssistantLayout,
  DismissButon,
  FlexContainer,
  PreviewWrapper,
  ScaleTemplateAnimation,
  UseButton,
  UseDismissWrapper,
  SlideUpAnimation,
  Loader,
  CartNavigationWrapper,
  CartSummary
} from "./AdAssistant.styled";

import Cart from "./cart/Cart";
import EditionModal from "./edition-modal/EditionModal";

import { useAdAssistantDimensions } from "./hooks/useAdAssistantDimensions";
import AdAssistantStore from "./AdAssistantStore";
import { withRefluxStore } from "../../Stores";

import AllVariantsModal from "./all-variants-modal/AllVariantsModal";
import { Trans, useTranslation } from "react-i18next";
// import { SaveIcon, ShareIcon } from "../../Components/Icons";
import { Divider } from "./cart/Cart.styled";
import Actions from "../../Actions";

import OrientationsTemplatePreview from "./previews/orientations-template-preview/OrientationsTemplatePreview";
import AdAssistantHeader from "./ad-assistant-header/AdAssistantHeader";
import { orientationsOptions, VIEW_SELECTOR } from "./constants";
import { ActionsAdAssistant } from "../../Actions/ActionsAdAssistant/ActionsAdAssistant";
import SaveNavigation from "./save-navigation/SaveNavigation";
import { withRouter } from "react-router-dom";
import { useAdUpdate } from "./hooks/useAdUpdate";
import { useSaveCampaign } from "./hooks/useSaveCampaign";
import { useUpdateCombinationData } from "./hooks/useUpdateCombinationData";
import { UsedIcon } from "../../Components/Icons";

export const TEMPLATES = {
  ...IMAGE_TEMPLATES,
  ...VIDEO_TEMPLATES
};

export const TEMPLATE_INFO = {
  ...IMAGE_TEMPLATES_INFO,
  ...VIDEO_TEMPLATES_INFO
};

const AdAssistant = ({
  // Route
  match,
  // Store
  jobTitle = "",
  userProfile,
  allAdCombinations,
  usedAds,
  currentAd,
  text,
  campaignData
}) => {
  const dimensions = useAdAssistantDimensions();
  const { t } = useTranslation();
  const { campaignId } = match.params;

  const TemplateComponent = useMemo(
    () => TEMPLATES[currentAd?.mediaAssets?.vertical?.templateId] || OneToneImage,
    [currentAd]
  );

  const [dimensionsPreview, setDimensionsPreview] = useState(null);
  const [editionModal, setEditionModal] = useState({ isModalOpen: false, type: "" });
  const [isAdPreviewHovered, setIsAdPreviewHovered] = useState(false);
  const [orientationSelected, setOrientationSelected] = useState(orientationsOptions[0]);
  const [viewSelected, setViewSelected] = useState(VIEW_SELECTOR.SINGLE);
  const [selectedAds, setSelectedAds] = useState(usedAds.length);

  const isTemplateVideo = currentAd?.mediaAssets?.[orientationSelected?.value]?.type === "video";
  const showLoader = allAdCombinations.length === 0;
  const mediaAssets = currentAd?.mediaAssets;
  const currentAdMediaAsset = mediaAssets?.[orientationSelected?.value];
  const autoCroppingImage =
    currentAdMediaAsset?.type === "video"
      ? currentAdMediaAsset?.inputProps?.video
      : currentAdMediaAsset?.inputProps?.backgroundImage;
  const videoDuration = currentAdMediaAsset?.inputProps?.duration || 0;

  const adIndexCount = allAdCombinations.findIndex((ad) => ad.id === currentAd.id) + 1;
  const totalAds = allAdCombinations.length || 0;

  const { updateAd, updateMediaAssets } = useAdUpdate(currentAd, allAdCombinations, userProfile);
  const saveCampaign = useSaveCampaign(campaignId);

  const { updateCombinationData } = useUpdateCombinationData({
    currentAd,
    userProfile,
    dimensions,
    allAdCombinations
  });

  // Update dimensionsPreview when orientation or currentAd changes
  useEffect(() => {
    const currentAdInformed = currentAd && Object.keys(currentAd).length > 0;
    if (currentAdInformed && orientationSelected) {
      const templateId = currentAd.mediaAssets[orientationSelected?.value]?.templateId;
      const isMultiTemplate = TEMPLATE_INFO[templateId]?.orientation === "multi";

      let dimensionTemplateKey = dimensions[templateId] || dimensions.default;

      if (isMultiTemplate) {
        dimensionTemplateKey =
          dimensions[templateId][orientationSelected?.value] || dimensions.default;
      }

      setDimensionsPreview(dimensionTemplateKey);
    }
  }, [currentAd, orientationSelected, dimensions]);

  // Reset selected ads after optimistic update
  useEffect(() => {
    setSelectedAds(usedAds.length);
  }, [usedAds]);

  const handleOrientationChange = (selectedOrientation) => {
    setOrientationSelected(selectedOrientation);
  };

  const handleEditionModalToggle = ({ type = "" }) => {
    const { isModalOpen } = editionModal;
    const newModalState = !isModalOpen;
    setEditionModal({ isModalOpen: newModalState, type });
    ActionsAdAssistant.setEditModalOpen(newModalState);
  };

  // This makes the UI update optimistically, smooth UX
  const setOptimisticCurrentAd = () => {
    ActionsAdAssistant.setCurrentAd(allAdCombinations[adIndexCount]);
  };

  const useAd = () => {
    updateAd({
      text: { long: text.long },
      status: "used"
    });

    setOptimisticCurrentAd();
    setSelectedAds(selectedAds + 1);
  };

  const dismissAd = () => {
    updateAd({
      text: { long: text.long },
      status: "dismissed"
    });

    setOptimisticCurrentAd();
  };

  const handleSaveCampaign = async (data) => {
    try {
      await saveCampaign({ ...data, step: "preview" });
    } catch (error) {
      Actions.addNotification(t("Saved failed"), "error");
    }
  };

  const handleNewInputs = async (newInputs) => {
    const newTeaserTextValue = { long: newInputs, short: newInputs };
    const newCampaignData = { defaultText: newTeaserTextValue };

    if (currentAd.status !== "temp") {
      ActionsAdAssistant.updateAd(
        { updatedBy: userProfile.userName, text: newTeaserTextValue },
        currentAd.id
      );

      const createSuccessfully = ActionsAdAssistant.updateAd.completed.listen((result) => {
        const { payload } = result;

        ActionsAdAssistant.setCurrentAd(payload);
        ActionsAdAssistant.getAdCombinations(campaignId);
        handleSaveCampaign(newCampaignData);
        createSuccessfully();
      });
    } else {
      await handleSaveCampaign(newCampaignData);
    }
  };

  const handleNewInputProps = (
    newInputs,
    showNotification,
    orientation = { value: "all", label: "All" }
  ) => {
    const newInputsObject = newInputs.reduce((acc, { key, value }) => {
      acc[key] = value;
      return acc;
    }, {});

    const updateOrientation = (orientationKey) => {
      updatedMediaAssets[orientationKey] = {
        ...mediaAssets[orientationKey],
        inputProps: { ...mediaAssets[orientationKey].inputProps, ...newInputsObject }
      };
    };

    const updatedMediaAssets = { ...mediaAssets };

    if (orientation.value === "all") {
      updateOrientation("vertical");
      updateOrientation("portrait");
      updateOrientation("landscape");
    } else {
      updateOrientation(orientation.value);
    }

    const newMediaAsset =
      orientation.value === "all"
        ? updatedMediaAssets
        : { [orientation.value]: updatedMediaAssets[orientation.value] };

    updateMediaAssets({ updatedMediaAssets: newMediaAsset, showNotification });
  };

  const handleNewImage = (newImage) => {
    updateCombinationData({ newAsset: newImage });
  };

  const handleViewChange = (view) => {
    setViewSelected(view);
  };

  const isCurrentAdUsed = currentAd.status === "used";
  const targetCities = campaignData?.locations?.map(({ location }) => location).join(", ");

  // Handle visibility of components
  const showEditionModal = editionModal.isModalOpen;
  const showAllDrafts = viewSelected === VIEW_SELECTOR.ALL_DRAFTS && !showEditionModal;
  const showSingleAd = viewSelected === VIEW_SELECTOR.SINGLE && !showEditionModal;

  return (
    <AdAssistantLayout>
      <AdAssistantHeader
        jobTitle={jobTitle}
        viewSelected={viewSelected}
        emitViewChange={handleViewChange}
        orientationsOptions={orientationsOptions}
        orientationSelected={orientationSelected}
        handleOrientationChange={handleOrientationChange}
        adIndexCount={adIndexCount}
        totalAds={totalAds}
      />
      {showSingleAd && (
        <AdAssistantBody>
          <div>
            {currentAd && (
              <PreviewWrapper>
                <FlexContainer minHeight={"466px"}>
                  <AnimatePresence>
                    <motion.div
                      {...ScaleTemplateAnimation}
                      width={dimensionsPreview?.width}
                      height={dimensionsPreview?.height}
                    >
                      {showLoader ? (
                        <motion.div
                          key={`${adIndexCount}-${orientationSelected?.value}`}
                          {...ScaleTemplateAnimation}
                        >
                          <Loader />
                        </motion.div>
                      ) : (
                        <motion.div
                          key={`${adIndexCount}-${orientationSelected?.value}`}
                          {...ScaleTemplateAnimation}
                        >
                          <OrientationsTemplatePreview
                            isTemplateVideo={isTemplateVideo}
                            dimensionsPreview={dimensionsPreview}
                            templateInputProps={
                              currentAd?.mediaAssets?.[orientationSelected?.value].inputProps
                            }
                            autoCroppingImage={autoCroppingImage}
                            orientationSelected={orientationSelected?.value}
                            videoDuration={videoDuration}
                            TemplateComponent={TemplateComponent}
                            toggleEditionModal={handleEditionModalToggle}
                            setIsAdPreviewHovered={setIsAdPreviewHovered}
                            isAdPreviewHovered={isAdPreviewHovered}
                            editedAd={currentAd}
                            userProfile={userProfile}
                          />
                        </motion.div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </FlexContainer>

                <UseDismissWrapper>
                  <DismissButon $isUsed={isCurrentAdUsed} onClick={dismissAd} disabled={showLoader}>
                    {t("Dismiss")}
                  </DismissButon>
                  {/* <ShareIcon /> */}
                  <Divider />
                  {/* <SaveIcon /> */}
                  <UseButton $isUsed={isCurrentAdUsed} onClick={useAd} disabled={showLoader}>
                    {isCurrentAdUsed ? (
                      <>
                        <UsedIcon
                          colorStrokeCheck={`var(--primary-black)`}
                          colorStrokeCircle="transparent"
                        />{" "}
                        {t("Keep it")}
                      </>
                    ) : (
                      t("Use it")
                    )}
                  </UseButton>
                </UseDismissWrapper>
              </PreviewWrapper>
            )}
          </div>

          <div>
            <CartSummary>
              <Trans
                i18nKey="campaign_sentence"
                values={{ jobTitle, cities: targetCities }}
                components={{
                  openPosition: <span id="highlight" />,
                  jobTitle: <span id="job-title" />,
                  cities: <span id="city-name" />
                }}
              />
            </CartSummary>
            <CartNavigationWrapper>
              <Cart selectedItems={selectedAds} />
              <SaveNavigation
                selectedItems={selectedAds}
                emitSaveCampaign={(data) => handleSaveCampaign(data)}
                navigationUrl={`/assistant/overview/${campaignId}`}
                navigationText={t("Accept selection")}
              />
            </CartNavigationWrapper>
          </div>
        </AdAssistantBody>
      )}
      <AnimatePresence>
        {showAllDrafts && (
          <AllVariantsModal
            toggleEditionModal={handleEditionModalToggle}
            orientationsOptions={orientationsOptions}
            viewSelected={viewSelected}
            handleViewChange={handleViewChange}
            emitSaveCampaign={handleSaveCampaign}
            campaignId={campaignId}
            emitUseAd={useAd}
            emitDismissAd={dismissAd}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showEditionModal && (
          <EditionModal
            toggleModal={handleEditionModalToggle}
            editionModalType={editionModal.type}
            saveNewImage={(imageUrl) => handleNewImage(imageUrl)}
            saveNewData={(newData) => handleNewInputs(newData)}
            saveNewInputProps={handleNewInputProps}
          />
        )}
      </AnimatePresence>
    </AdAssistantLayout>
  );
};

export default withRouter(
  withRefluxStore(
    AdAssistant,
    [AdAssistantStore],
    ["jobTitle", "userProfile", "allAdCombinations", "usedAds", "currentAd", "text", "campaignData"]
  )
);
